import { useState, useEffect } from 'react';
import { RouteComponentProps } from '@reach/router';
import { useLazyQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '@calefy-inc/authentication';
import { Form, Select, TextField } from '@calefy-inc/informedMaterial';
import { graphql } from '../../gql';
import { SearchFinalFormsQuery } from '../../gql/graphql';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { DisplayInlineError } from '../common/ErrorHandling';
import { AdminDisplayFinalForms } from './AdminDisplayFinalForms';
import { ALL_ORGANIZATIONS } from '../../queries';
import { withFormStyles, WithFormStyles } from '../../util/withFormStyles';
import { AdminPanelDisplayFormDialog } from './AdminPanelDisplayFormDialog';

type SearchFinalFormsEdges = NonNullable<
  NonNullable<SearchFinalFormsQuery['searchFinalForms']>['edges']
>;
type SearchFinalFormsEdge = ArrayElement<SearchFinalFormsEdges>;

export type FormNode = NonNullable<SearchFinalFormsEdge['node']>;

const DEFAULT_FORMS_PER_PAGE = 20;

interface SearchFinalFormsProps extends RouteComponentProps, WithFormStyles {}

export const SearchFinalForms = withFormStyles()(
  ({ classes }: SearchFinalFormsProps) => {
    const { token } = useAuth();
    const [forms, setForms] = useState<Array<FormNode> | null>(null);
    const [endCursor, setEndCursor] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useState<{
      organizationId?: string;
      internalName?: string;
    }>({});
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
    const [selectedFormTitle, setSelectedFormTitle] = useState<
      string | undefined
    >(undefined);
    const [searchFinalForms, { loading, error, data }] =
      useLazyQuery(SEARCH_FINAL_FORMS);
    const [getOrganizations, { data: organizationData }] =
      useLazyQuery(ALL_ORGANIZATIONS);

    useEffect(() => {
      if (token) {
        getOrganizations({ variables: { token } });
      }
    }, [token, getOrganizations]);

    const handleSearch = (formState: {
      values: { organizationId?: string; internalName?: string };
    }) => {
      if (token) {
        const { values } = formState;
        const organizationId = values.organizationId
          ? String(values.organizationId)
          : undefined;
        const internalName = values.internalName
          ? String(values.internalName)
          : undefined;

        setForms(null);
        setEndCursor(null);
        setHasNextPage(false);
        setSearchParams({ organizationId, internalName });

        searchFinalForms({
          variables: {
            token,
            first: DEFAULT_FORMS_PER_PAGE,
            organizationId,
            internalName,
          },
        });
      }
    };

    const handleLoadMore = () => {
      if (token && endCursor) {
        searchFinalForms({
          variables: {
            token,
            after: endCursor,
            first: DEFAULT_FORMS_PER_PAGE,
            organizationId: searchParams.organizationId,
            internalName: searchParams.internalName,
          },
        });
      }
    };

    const handleFormDelete = (deletedFormId: string) => {
      setForms((prevForms) => {
        if (!prevForms) {
          return null;
        }
        return prevForms.filter((form) => form.id !== deletedFormId);
      });
    };

    // Extract nodes from the data when it's available
    useEffect(() => {
      if (data?.searchFinalForms?.edges) {
        const nodes = data.searchFinalForms.edges
          .map((edge: SearchFinalFormsEdge) => edge?.node)
          .filter(
            (node: FormNode | null | undefined): node is FormNode =>
              node !== null && node !== undefined,
          );

        // If we already have forms, we're loading more (appending)
        // Otherwise, this is the initial load
        setForms((prevForms) => {
          if (prevForms && prevForms.length > 0) {
            // Loading more - append to existing
            return [...prevForms, ...nodes];
          } else {
            // Initial load - replace
            return nodes;
          }
        });

        // Update pagination info
        if (data.searchFinalForms.pageInfo) {
          setEndCursor(data.searchFinalForms.pageInfo.endCursor ?? null);
          setHasNextPage(data.searchFinalForms.pageInfo.hasNextPage ?? false);
        }
      }
    }, [data]);

    return (
      <div>
        <h1>Search Final Forms</h1>
        <Form className={classes.form} onSubmit={handleSearch}>
          {organizationData ? (
            <Select
              name='organizationId'
              label='Organization'
              className={classes.selectField}
              options={[
                { value: undefined, label: 'All Organizations' },
                ...(organizationData.allOrganizations
                  ? organizationData.allOrganizations
                      .filter(
                        (org): org is NonNullable<typeof org> =>
                          org?.id !== null &&
                          org?.id !== undefined &&
                          org?.name !== null &&
                          org?.name !== undefined,
                      )
                      .map((org) => ({
                        value: org.id,
                        label: org.name,
                      }))
                  : []),
              ]}
            />
          ) : null}
          <TextField
            name='internalName'
            label='Internal Name'
            className={classes.textfield}
          />
          <Button
            type='submit'
            variant='contained'
            disabled={loading || !token}
          >
            Search
          </Button>
        </Form>
        {loading ? <CircularProgress /> : null}
        <DisplayInlineError error={error} />
        {forms ? (
          <AdminDisplayFinalForms
            forms={forms}
            onFormClick={(formId) => {
              const form = forms.find((f) => f.id === formId);
              setSelectedFormId(formId);
              setSelectedFormTitle(
                form?.internalName ||
                  form?.policy?.displayName ||
                  form?.businessLine?.displayName,
              );
              setDialogOpen(true);
            }}
          />
        ) : null}
        {hasNextPage && forms ? (
          <Button
            variant='outlined'
            onClick={handleLoadMore}
            disabled={loading || !token}
            sx={{ mt: 2 }}
          >
            Load More
          </Button>
        ) : null}
        <AdminPanelDisplayFormDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedFormId(null);
            setSelectedFormTitle(undefined);
          }}
          formId={selectedFormId}
          formTitle={selectedFormTitle}
          onFormDelete={handleFormDelete}
        />
      </div>
    );
  },
);

export const SEARCH_FINAL_FORMS = graphql(`
  query searchFinalForms(
    $token: String!
    $after: String
    $first: Int
    $organizationId: ID
    $internalName: String
  ) {
    searchFinalForms(
      token: $token
      after: $after
      first: $first
      organizationId: $organizationId
      internalName: $internalName
    ) {
      edges {
        node {
          id
          internalName
          required
          businessLine {
            id
            displayName
            internalName
          }
          policy {
            id
            displayName
            internalName
          }
          organization {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);
